/**
 * EventCompanions — the canonical product/companion card grid.
 *
 * Renders ALL of an event's products/features as visual cards across
 * three surfaces:
 *   • Organiser Dashboard (Mode 2 — graphite register)
 *   • EventPage Setup tab (Mode 2 — graphite operational)
 *   • Guest dashboard / landing surfaces (Mode 1 — walnut editorial)
 *
 * Per Craig's 2026-05-13 evening lock:
 *   • Distinguish "included in edition" vs "enabled" vs "commissioned
 *     companion" — but NEVER via the badges "PRO", "UPGRADE",
 *     "PREMIUM" (banned vocabulary). Use restrained editorial labels.
 *   • A companion that hasn't been commissioned shows a holding state,
 *     not an upsell.
 *   • If checkout is incomplete (`event.payment_status === 'pending'`)
 *     the cards render as locked-pre-commission holding states — the
 *     host can SEE them but not operate them.
 *
 * The catalogue lives ONCE here so the three surfaces stay in lockstep.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { MODE_TWO } from './ModeTwoPrimitives';


// ── The catalogue — single source of truth ───────────────────────
//
// Each entry: { id, label, line, slug, route(event) → string,
//               includedFor: predicate(event) → bool,
//               enabledFor:  predicate(event) → bool }
//
// `includedFor` = the edition the host commissioned already bundles
// this product. `enabledFor` = the product has been explicitly
// switched on for this event. A product can be enabled without
// being included (commissioned companion), or included without
// being enabled (edition default that the host hasn't toggled).
const COMPANIONS = [
  // ─── KEEPSAKE TIER (always included) ─────────────────────────────
  // Ordered by emotional flow on the night, not technical hierarchy.
  {
    id: 'memory_snap',
    label: 'Memory Snap',
    line: 'A polaroid moment from each guest.',
    route: (e) => `/event/${e.slug}/memory-snap`,
    includedFor: () => true,
    enabledFor:  () => true,
  },
  {
    id: 'memory_wall',
    label: 'Memory Wall',
    line: 'A living wall of every photo, message and video.',
    route: (e) => `/event/${e.slug}/memory-wall`,
    includedFor: () => true,
    enabledFor:  () => true,
  },
  {
    id: 'highlight_reel',
    label: 'Highlight Reel',
    line: 'A short, composed cut of the night.',
    // Keepsake includes Highlight Reel; the dedicated render surface
    // arrives in a follow-up. Until then we land on the wall with a
    // quiet "Your highlight reel is being composed ✦" notice so the
    // card never dead-ends.
    route: (e) => `/event/${e.slug}/memory-wall?notice=highlight-composing`,
    includedFor: () => true,
    enabledFor:  () => true,
  },
  {
    id: 'recap_letters',
    label: 'Recap Letters',
    line: 'A letter to every guest after the night settles.',
    route: (e) => `/event/${e.slug}/memory-recap`,
    includedFor: () => true,
    enabledFor:  () => true,
  },
  {
    id: 'archive_shelf',
    label: 'Archive Shelf',
    line: 'Chapters and letters from the night, on a shelf.',
    route: (e) => `/event/${e.slug}/archive`,
    includedFor: () => true,
    enabledFor:  () => true,
  },
  {
    id: 'qr_upload',
    label: 'QR Upload',
    line: 'Guests scan and add photos and videos to the wall.',
    route: (e) => `/event/${e.slug}/memory-wall?focus=qr`,
    includedFor: () => true,
    enabledFor:  () => true,
  },

  // ─── ROOM TIER ───────────────────────────────────────────────────
  {
    // Booth OS — the venue kiosk. Listed first in the Room tier
    // because it is the marquee feature and the "physical entrance
    // into EventDrop." Opens the kiosk surface itself so the host
    // can preview exactly what guests will see at the venue.
    id: 'booth_os',
    label: 'Booth OS',
    line: 'Venue kiosk experience — photos, video messages, Memory Snaps and the Guestbook from one tablet at the room.',
    route: (e) => `/booth-os/${e.event_id}`,
    includedFor: (e) => !!e.booth_os_purchased ||
      ['edition_room', 'edition_archive'].includes((e.package_type || '').toLowerCase()),
    enabledFor:  (e) => !!e.booth_os_purchased ||
      ['edition_room', 'edition_archive'].includes((e.package_type || '').toLowerCase()),
  },
  {
    id: 'pocket_booth',
    label: 'Pocket Booth',
    line: 'Phone-based booth guests can open anywhere during the night.',
    route: (e) => `/event/${e.slug}/pocket-booth`,
    includedFor: (e) => !!e.pocket_booth_purchased,
    enabledFor:  (e) => !!e.pocket_booth_purchased,
  },
  {
    id: 'audio_guestbook',
    label: 'Audio / Video Guestbook',
    line: 'Guests record a short message to the night.',
    route: (e) => `/event/${e.slug}/guestbook`,
    includedFor: (e) => !!e.guestbook_purchased,
    enabledFor:  (e) => !!e.guestbook_purchased,
  },
  {
    id: 'mega_missions',
    label: 'Mega Missions',
    line: 'Timed quests that stir the room into motion.',
    // Mega Missions render inside the guest's own page (the
    // GuestHome module). Card opens the guest's space with the
    // missions section focused.
    route: (e) => `/event/${e.slug}/g/${e.__guest_id || ''}?focus=missions`,
    includedFor: (e) => !!e.challenges_purchased,
    enabledFor:  (e) => !!e.challenges_purchased,
  },
  {
    id: 'invites',
    label: 'Invites & RSVP',
    line: 'Sent quietly, returned with intent.',
    route: (e) => `/event/${e.slug}/invites`,
    includedFor: (e) => !!e.invites_purchased,
    enabledFor:  (e) => !!e.invites_purchased,
  },

  // ─── ARCHIVE TIER ───────────────────────────────────────────────
  {
    id: 'spin_360',
    label: 'Spin Film 360',
    line: 'A slow-motion 360 spin for every guest.',
    route: () => `/spin`,
    includedFor: (e) => !!e.spin_360_purchased,
    enabledFor:  (e) => !!e.spin_360_purchased,
  },
  {
    id: 'event_film',
    label: 'Event Film',
    line: 'A composed cinematic record of the night.',
    route: (e) => `/film/${e.slug}`,
    includedFor: (e) => !!e.film_purchased,
    enabledFor:  (e) => !!e.film_purchased,
  },
  // Lens Notes ✦ — Archive-exclusive (iter 168). Positioned HIGH inside
  // Archive (just under Event Film) because the moment guests see it
  // they instantly understand the tier difference: this is the first
  // companion that suppliers can't easily replicate with WhatsApp +
  // Drive — proprietary emotional interaction.
  {
    id: 'lens_notes',
    label: 'Lens Notes ✦',
    line: 'Handwritten video memories from the people who lived the night beside you.',
    route: (e) => `/lens-note/${e.slug}`,
    // Iter171 fix: the canonical truth is the computed `features`
    // list (server-side merges PACKAGE_ENTITLEMENTS + flags + the
    // universal-features helper + reconcile-on-read). Flag fields
    // like `lens_notes_purchased` are stripped from the API response
    // so reading them client-side returns undefined → events
    // showed "Not yet" even on Archive editions. Both checks now
    // route through the features array; flags kept as a back-compat
    // fallback for any code path that still hydrates them.
    includedFor: (e) => (e.features || []).includes('lens_notes') || !!e.lens_notes_purchased,
    enabledFor:  (e) => (e.features || []).includes('lens_notes') || !!e.lens_notes_purchased,
  },
  // Drop One Word ✦ — universal (iter 170). Appears in every edition's grid.
  {
    id: 'drop_one_word',
    label: 'Drop One Word ✦',
    line: 'A living atmosphere from the feelings shared throughout the night.',
    route: (e) => `/one-word/${e.slug}`,
    includedFor: (e) => (e.features || []).includes('drop_one_word') || !!e.drop_one_word_purchased,
    enabledFor:  (e) => (e.features || []).includes('drop_one_word') || !!e.drop_one_word_purchased,
  },
  {
    id: 'memory_guestbook',
    label: 'Memory Guestbook',
    line: 'A printed keepsake of the night, mailed afterwards.',
    // iter180 — focus key uses underscore to match FOCUS_TO_TAB in
    // EventPage. Previously this used 'memory-book' (hyphen), which
    // missed the lookup, left the tab unchanged, and only updated the
    // URL — causing the page to re-render and jump to the top
    // without opening the Archive tab.
    route: (e) => `/event/${e.slug}?focus=memory_book`,
    includedFor: (e) => !!e.memory_book_purchased,
    enabledFor:  (e) => !!e.memory_book_purchased,
  },
  // ── Remember Us ✦ — universal participation surface (iter195) ──
  // The shared atmosphere wall. Universal across editions because
  // it's the participation substrate — Belief 2. Surfaced as a
  // companion card so every host can find it, but Leavers Night
  // dashboards also elevate it via the dedicated "Leave your mark"
  // section above the companions grid (iter195).
  {
    id: 'remember_us',
    label: 'Remember Us ✦',
    line: 'A shared wall the room writes together — messages, drawings, signatures, fragments.',
    route: (e) => `/remember-us/${e.slug}`,
    includedFor: () => true,
    enabledFor:  () => true,
  },
  // ── The Final Night ✦ — archival companion (iter200b) ──────────
  // The Chronicle is not generated afterward from uploads; it is
  // present *during* the evening as a quiet archival layer that
  // gathers the room and composes into a printed remembrance
  // after the night settles. Sits AFTER Remember Us so the
  // emotional hierarchy stays:
  //   1. participation (Remember Us)
  //   2. atmosphere (Memory Wall, Drop One Word)
  //   3. preservation (The Final Night)
  // Bundled in Leavers Night and Archive; deliberately hidden from
  // Keepsake (which does not commission it). The Room sees it as
  // a commissioned companion (latent until upgrade) — Booth OS
  // dims it for Room until the "OPTIONAL in The Room" decision
  // lands; the dashboard companion grid keeps it visible as the
  // archival narrative anchor.
  {
    id: 'final_night',
    label: 'The Final Night ✦',
    line: 'A printed remembrance of the room, composed from the moments everyone left behind.',
    route: (e) => `/event/${e.slug}/final-night`,
    includedFor: (e) => ['edition_leavers_night', 'edition_archive']
      .includes((e.package_type || '').toLowerCase()),
    enabledFor:  (e) => ['edition_leavers_night', 'edition_archive']
      .includes((e.package_type || '').toLowerCase()),
  },
  // ── Silent Film ✦ — poetic fragment artefact (iter208) ─────────
  // 20–45s vertical black & white recap. Distinct from the Final
  // Night (printed chronicle) and the Documentary (long-form
  // narrative). Sits BETWEEN them in the archive ordering so the
  // three siblings render in the canonical Final Night → Silent
  // Film → Documentary sequence everywhere in the system. Same
  // bundling rules as Final Night: Leavers Night + Archive.
  {
    id: 'silent_film',
    label: 'Silent Film ✦',
    line: 'A short black & white fragment of the evening, remembered like cinema.',
    route: (e) => `/event/${e.slug}/silent-film`,
    includedFor: (e) => ['edition_leavers_night', 'edition_archive']
      .includes((e.package_type || '').toLowerCase()),
    enabledFor:  (e) => ['edition_leavers_night', 'edition_archive']
      .includes((e.package_type || '').toLowerCase()),
  },
  // ── The Documentary ✦ — long-form emotional narrative (iter206) ─
  // The full emotional return: 3–6 minutes assembled from photos,
  // voices, group moments and Lens Notes across three movements
  // (Before We Leave / The Final Night / What Remains). The deepest
  // preservation artefact — sits LAST in the archive ordering so it
  // lands as the emotional aftermath on Leavers surfaces (where
  // the cinematic film cuts are absent, Documentary naturally
  // becomes the final row). Same bundling as Final Night.
  {
    id: 'documentary',
    label: 'The Documentary ✦',
    line: 'The year group telling its own story back to itself.',
    route: (e) => `/event/${e.slug}/documentary`,
    includedFor: (e) => ['edition_leavers_night', 'edition_archive']
      .includes((e.package_type || '').toLowerCase()),
    enabledFor:  (e) => ['edition_leavers_night', 'edition_archive']
      .includes((e.package_type || '').toLowerCase()),
  },
];


// ── iter195 — Edition coherence map ─────────────────────────────
// Each edition declares which companion ids it deliberately HIDES.
// This is the negative-space contract: a Leavers Night room must
// not expose Spin Film 360 / Event Film / Highlight Reel / Lens
// Notes / Memory Guestbook, even though some of those have an
// "always included" predicate inherited from the wedding-era
// catalogue. The brief is explicit: those belong to The Archive ✦
// (wedding-only). Inclusion here would erode The Archive's
// narrative gravity AND make Leavers Night feel configurable
// instead of pre-composed.
const EDITION_HIDDEN_COMPANIONS = {
  edition_leavers_night: new Set([
    'highlight_reel',
    'spin_360',
    'event_film',
    'lens_notes',
    'memory_guestbook',
  ]),
  // iter200b — Keepsake does not commission The Final Night ✦; it
  // is bundled with Leavers Night and Archive only. Room's status
  // is "OPTIONAL (future decision)" per the brief, so it stays
  // hidden from the Room dashboard companion grid until that
  // decision lands. Booth OS dims the same card for Room under
  // the canonical "Available inside The Archive ✦" register.
  edition_keepsake: new Set(['final_night']),
  edition_room:     new Set(['final_night']),
};

const visibleCompanionsFor = (event) => {
  const pkg = (event?.package_type || '').toLowerCase();
  const hidden = EDITION_HIDDEN_COMPANIONS[pkg];
  if (!hidden) return COMPANIONS;
  return COMPANIONS.filter((c) => !hidden.has(c.id));
};

// ── iter208 — Companion → emotional layer mapping ───────────────
// Mirrors the 4-section taxonomy in /shared/packageGuestProducts.js
// so the operator and guest dashboards group companions under the
// same hierarchy that Booth OS, the Schools page and the homepage
// already use. Companions not present here trail at the end as an
// unsectioned "Other ✦" row — we deliberately do NOT invent a
// fallback layer for them; if a future companion is added it must
// be placed here explicitly (constitutional event, not implicit
// drift).
const COMPANION_LAYER = {
  // CAPTURE THE ROOM ✦
  pocket_booth:     'capture',
  memory_snap:      'capture',
  spin_360:         'capture',
  qr_upload:        'capture',
  booth_os:         'capture',
  // LEAVE SOMETHING BEHIND ✦
  lens_notes:       'leave_behind',
  remember_us:      'leave_behind',
  drop_one_word:    'leave_behind',
  audio_guestbook:  'leave_behind',
  memory_guestbook: 'leave_behind',
  recap_letters:    'leave_behind',
  // SEE THE ROOM FORMING ✦
  memory_wall:      'see_forming',
  // PRESERVE / WHAT REMAINS ✦  (Documentary always last — see
  // COMPANION_LAYER_ORDER below)
  final_night:      'preserve',
  silent_film:      'preserve',
  documentary:      'preserve',
  highlight_reel:   'preserve',
  event_film:       'preserve',
  archive_shelf:    'preserve',
  // Operational, not emotional — these don't belong in the emotional
  // spine. They trail in a small operations row when present.
  mega_missions:    'operations',
  invites:          'operations',
};

// Canonical ordering inside each layer. Companion ids listed below
// in the order they should render. Ids absent from a layer's list
// trail at the end in catalogue order.
const COMPANION_LAYER_ORDER = {
  capture:      ['pocket_booth', 'memory_snap', 'spin_360', 'qr_upload', 'booth_os'],
  leave_behind: ['lens_notes', 'remember_us', 'drop_one_word', 'audio_guestbook', 'memory_guestbook', 'recap_letters'],
  see_forming:  ['memory_wall'],
  // Constitutional: Final Night → Silent Film → Documentary, then
  // the cinematic cuts. On Leavers (highlight_reel and event_film
  // are hidden), Documentary naturally becomes the last row of the
  // archive layer — the deepest emotional landing. See iter208.
  preserve:     ['final_night', 'silent_film', 'documentary', 'highlight_reel', 'event_film', 'archive_shelf'],
  operations:   ['mega_missions', 'invites'],
};

// Layer chrome — titles per register. iter208 brief: aftermath for
// guest-facing surfaces, stewardship for operator-facing.
const LAYER_CHROME = {
  capture:      { aftermath: 'Capture the room ✦',      stewardship: 'Capture the room ✦',      subtitle: 'Moments as they happen.' },
  leave_behind: { aftermath: 'Leave something behind ✦', stewardship: 'Leave something behind ✦', subtitle: 'Words, voices, handwriting, feelings.' },
  see_forming:  { aftermath: 'See the room forming ✦',   stewardship: 'See the room forming ✦',   subtitle: 'The atmosphere, live.' },
  preserve:     { aftermath: 'What remains ✦',           stewardship: 'Preserve the night ✦',     subtitle: 'What the room becomes afterwards.' },
  operations:   { aftermath: 'Operations ✦',             stewardship: 'Operations ✦',             subtitle: 'Behind the evening.' },
};

const LAYER_RENDER_ORDER = ['capture', 'leave_behind', 'see_forming', 'preserve', 'operations'];

/**
 * Group a visible-companion list into the iter208 emotional layers.
 * @param {Array} companions — output of visibleCompanionsFor(event)
 * @param {'aftermath'|'stewardship'} register
 * @returns {Array<{id, title, subtitle, items}>}
 */
const groupCompanionsByLayer = (companions, register = 'aftermath') => {
  if (!Array.isArray(companions) || companions.length === 0) return [];
  const byId = new Map(companions.map((c) => [c.id, c]));
  const out = [];
  for (const layerId of LAYER_RENDER_ORDER) {
    const order = COMPANION_LAYER_ORDER[layerId] || [];
    const items = [];
    for (const cid of order) {
      const c = byId.get(cid);
      if (c) items.push(c);
    }
    // Append any companions assigned to this layer but not in the
    // explicit order (defensive — should be empty in practice).
    for (const c of companions) {
      if (COMPANION_LAYER[c.id] === layerId && !items.includes(c)) {
        items.push(c);
      }
    }
    if (items.length === 0) continue;
    const chrome = LAYER_CHROME[layerId];
    out.push({
      id: layerId,
      title: register === 'stewardship' ? chrome.stewardship : chrome.aftermath,
      subtitle: chrome.subtitle,
      items,
    });
  }
  return out;
};

// Tiny helper — Leavers Night dashboards take a deliberately
// simpler shape than wedding stewardship surfaces. Centralised
// so any future edition (graduation, sports day, etc.) follows
// the same contract instead of re-typing the comparison.
const isLeaversEdition = (event) =>
  (event?.package_type || '').toLowerCase() === 'edition_leavers_night';


// Compute the editorial status label for a single companion. Order
// of precedence is constitutional — included beats commissioned
// beats latent. Banned vocabulary ("PRO", "UPGRADE", "PREMIUM")
// is structurally absent from this map.
const statusFor = (companion, event) => {
  const included = companion.includedFor(event);
  const enabled  = companion.enabledFor(event);
  if (included)      return 'included';      // edition bundles this
  if (enabled)       return 'commissioned';  // explicit companion
  return 'latent';                            // not active for this event
};

const STATUS_COPY = {
  included:     'In the edition',
  commissioned: 'A companion',
  latent:       'Not yet',
};


// ─────────────────────────────────────────────────────────────────
//  Mode 2 — Organiser cards (graphite register language)
// ─────────────────────────────────────────────────────────────────
const ModeTwoCard = ({ companion, status, locked, href, testId }) => {
  const [pressed, setPressed] = useState(false);
  const isLatent = status === 'latent';
  const visuallyMuted = locked || isLatent;
  const Inner = (
    <div
      data-testid={testId}
      data-companion-id={companion.id}
      data-companion-status={status}
      data-companion-locked={locked ? '1' : '0'}
      onPointerDown={() => !visuallyMuted && setPressed(true)}
      onPointerUp={() => setPressed(false)}
      onPointerLeave={() => setPressed(false)}
      onPointerCancel={() => setPressed(false)}
      data-pressed={pressed ? '1' : '0'}
      style={{
        position: 'relative',
        padding: '18px 20px',
        background: MODE_TWO.bgPanel,
        border: `1px solid ${pressed ? MODE_TWO.goldPressed : MODE_TWO.hair}`,
        borderRadius: 4,
        opacity: visuallyMuted ? 0.55 : 1,
        cursor: visuallyMuted ? 'default' : 'pointer',
        transform: pressed ? 'translateY(1px)' : 'translateY(0)',
        transition: 'transform 220ms ease, border-color 220ms ease, opacity 220ms ease',
        display: 'flex', flexDirection: 'column', gap: 8, minHeight: 110,
      }}
    >
      <div style={{
        fontFamily: MODE_TWO.serif, fontSize: 15.5,
        color: MODE_TWO.ink, letterSpacing: '-0.005em',
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
        gap: 10,
      }}>
        <span>{companion.label}</span>
        <span style={{ color: MODE_TWO.goldSoft, fontSize: 12, marginTop: 2 }}>✦</span>
      </div>
      <div style={{
        fontFamily: MODE_TWO.serif, fontStyle: 'italic',
        fontSize: 12.5, color: MODE_TWO.inkMuted, lineHeight: 1.5,
        flex: 1,
      }}>{companion.line}</div>
      <div style={{
        marginTop: 4,
        fontFamily: MODE_TWO.sans,
        fontSize: 10, letterSpacing: '0.18em',
        textTransform: 'uppercase',
        color: locked ? MODE_TWO.inkFaint
              : status === 'included' ? MODE_TWO.gold
              : status === 'commissioned' ? MODE_TWO.inkMid
              : MODE_TWO.inkFaint,
      }}>{locked ? 'Pre-commission' : STATUS_COPY[status]}</div>
    </div>
  );
  if (visuallyMuted) return Inner;
  return <Link to={href} style={{ textDecoration: 'none', color: 'inherit' }}>{Inner}</Link>;
};


// ─────────────────────────────────────────────────────────────────
//  Mode 1 — Guest cards (walnut editorial warmth)
// ─────────────────────────────────────────────────────────────────
const ModeOneCard = ({ companion, status, href, testId }) => {
  const [pressed, setPressed] = useState(false);
  const isLatent = status === 'latent';
  if (isLatent) return null;   // Guests never see latent companions.
  return (
    <Link
      to={href}
      data-testid={testId}
      data-companion-id={companion.id}
      data-companion-status={status}
      onPointerDown={() => setPressed(true)}
      onPointerUp={() => setPressed(false)}
      onPointerLeave={() => setPressed(false)}
      data-pressed={pressed ? '1' : '0'}
      style={{
        display: 'block', textDecoration: 'none',
        padding: '20px 22px',
        background: 'rgba(255,251,242,0.7)',
        border: '1px solid rgba(184,151,98,0.28)',
        borderRadius: 3,
        color: '#1B140A',
        transform: pressed ? 'translateY(1px)' : 'translateY(0)',
        transition: 'transform 240ms ease, border-color 240ms ease, background 240ms ease',
        boxShadow: '0 1px 0 rgba(255,255,255,0.6) inset',
      }}
    >
      <div style={{
        fontFamily: 'Georgia, serif', fontSize: 17,
        color: '#1B140A', letterSpacing: '-0.005em',
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10,
      }}>
        <span>{companion.label}</span>
        <span style={{ color: 'rgba(184,151,98,0.85)', fontSize: 13, marginTop: 2 }}>✦</span>
      </div>
      <div style={{
        marginTop: 8,
        fontFamily: 'Georgia, serif', fontStyle: 'italic',
        fontSize: 13.5, color: 'rgba(40,32,18,0.65)',
        lineHeight: 1.6,
      }}>{companion.line}</div>
    </Link>
  );
};


// ─────────────────────────────────────────────────────────────────
//  Public components
// ─────────────────────────────────────────────────────────────────
export const EventCompanionsModeTwo = ({ event, testId = 'event-companions-mode-two' }) => {
  // Pre-commission holding state — host has not yet completed
  // checkout. Cards render but link out is suppressed.
  const locked = event?.payment_status === 'pending';
  const companions = visibleCompanionsFor(event);
  // iter208 — operator-facing surface uses the STEWARDSHIP register.
  const layers = groupCompanionsByLayer(companions, 'stewardship');
  return (
    <div data-testid={testId} style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
      {layers.map((layer) => (
        <section
          key={layer.id}
          data-testid={`companions-mode2-layer-${layer.id}`}
          style={{ display: 'flex', flexDirection: 'column', gap: 10 }}
        >
          <header style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <p
              data-testid={`companions-mode2-layer-title-${layer.id}`}
              style={{
                margin: 0,
                fontFamily: MODE_TWO.serif, fontStyle: 'italic',
                fontSize: 10.5, letterSpacing: '0.24em', textTransform: 'uppercase',
                color: MODE_TWO.inkMid,
              }}
            >
              {layer.title}
            </p>
            <p style={{
              margin: 0,
              fontFamily: MODE_TWO.serif, fontStyle: 'italic',
              fontSize: 12, color: MODE_TWO.inkMuted,
            }}>
              {layer.subtitle}
            </p>
          </header>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: 12,
          }}>
            {layer.items.map((c) => {
              const status = statusFor(c, event);
              return (
                <ModeTwoCard
                  key={c.id}
                  companion={c}
                  status={status}
                  locked={locked}
                  href={c.route(event)}
                  testId={`companion-mode2-${c.id}`}
                />
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
};


export const EventCompanionsModeOne = ({ event, testId = 'event-companions-mode-one' }) => {
  // Guests never see latent companions — only what is actually
  // available to them. Pre-commission state hides the whole grid
  // because guests should never encounter a partially-prepared
  // night.
  if (event?.payment_status === 'pending') return null;
  const companions = visibleCompanionsFor(event);
  // iter208 — guest-facing surface uses the AFTERMATH register so
  // the archive layer reads "What remains ✦" (matching homepage
  // + Schools page vocabulary).
  const layers = groupCompanionsByLayer(companions, 'aftermath');
  return (
    <div data-testid={testId} style={{ display: 'flex', flexDirection: 'column', gap: 36 }}>
      {layers.map((layer) => (
        <section
          key={layer.id}
          data-testid={`companions-mode1-layer-${layer.id}`}
          style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
        >
          <header style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <p
              data-testid={`companions-mode1-layer-title-${layer.id}`}
              style={{
                margin: 0,
                fontFamily: MODE_TWO.serif, fontStyle: 'italic',
                fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase',
                color: 'rgba(184,151,98,0.85)',
              }}
            >
              {layer.title}
            </p>
            <p style={{
              margin: 0,
              fontFamily: MODE_TWO.serif, fontStyle: 'italic',
              fontSize: 13, color: 'rgba(255,255,255,0.55)',
            }}>
              {layer.subtitle}
            </p>
          </header>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
            gap: 14,
          }}>
            {layer.items.map((c) => {
              const status = statusFor(c, event);
              return (
                <ModeOneCard
                  key={c.id}
                  companion={c}
                  status={status}
                  href={c.route(event)}
                  testId={`companion-mode1-${c.id}`}
                />
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
};


// ─────────────────────────────────────────────────────────────────
//  Compact ledger variant — for the organiser Dashboard register
// ─────────────────────────────────────────────────────────────────
//
// On the Dashboard, the row is already a quiet register entry. A
// full grid per row would feel busy. Instead, this compact strip
// shows the FIRST 4 active companions as small graphite chips,
// each linking to the corresponding focus inside EventPage. The
// host can always open the row to see the full grid.
export const EventCompanionsLedgerStrip = ({ event, testId }) => {
  const locked = event?.payment_status === 'pending';
  const active = visibleCompanionsFor(event)
    .map((c) => ({ companion: c, status: statusFor(c, event) }))
    .filter((x) => x.status !== 'latent')
    .slice(0, 4);
  if (active.length === 0) return null;
  return (
    <div
      data-testid={testId}
      style={{
        display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10,
      }}
    >
      {active.map(({ companion, status }) => (
        <span
          key={companion.id}
          data-testid={`ledger-chip-${companion.id}`}
          data-companion-status={status}
          style={{
            padding: '4px 10px',
            background: 'transparent',
            border: `1px solid ${MODE_TWO.hairSoft}`,
            borderRadius: 3,
            fontFamily: MODE_TWO.serif, fontStyle: 'italic',
            fontSize: 11.5,
            color: locked ? MODE_TWO.inkFaint
                  : status === 'included' ? MODE_TWO.inkMid
                  : MODE_TWO.inkMuted,
            letterSpacing: '0.01em',
            whiteSpace: 'nowrap',
          }}
        >{companion.label}</span>
      ))}
    </div>
  );
};


// Export the catalogue + status helpers so tests / future surfaces
// can reuse them without forking the source of truth.
export {
  COMPANIONS,
  statusFor,
  STATUS_COPY,
  EDITION_HIDDEN_COMPANIONS,
  visibleCompanionsFor,
  isLeaversEdition,
  // iter208 — taxonomy layering helpers (exported for tests + any
  // future surface that needs to read the spine).
  COMPANION_LAYER,
  COMPANION_LAYER_ORDER,
  LAYER_RENDER_ORDER,
  LAYER_CHROME,
  groupCompanionsByLayer,
};
